<template>
  <div class="inner">
    <div class="tw:grid tw:grid-cols-1 tw:items-center tw:gap-4 tw:md:grid-cols-12">
      <div class="tw:md:col-span-8">
        <h1 data-test="title">Carrier Analytics</h1>
      </div>
      <div class="tw:md:col-span-4">
        <TextField
          v-model="filterText"
          data-test="carrier-search-text"
          placeholder="Search Carriers"
          autocomplete="off"
          :appendIcon="searchIcon" />
      </div>
    </div>

    <div
      v-if="!loadingData && error"
      class="tw:mt-4 tw:alert tw:border-t tw:alert-error">
      {{ error }}
    </div>

    <div
      v-if="loadingData && !error"
      class="tw:space-y-2 tw:mt-10">
      <Skeleton
        v-for="i in 8"
        :key="i"
        shape="rectangle"
        width="100%"
        height="36px" />
    </div>

    <div
      v-else
      class="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-12">
      <div class="tw:md:col-span-5">
        <div class="tw:mt-6 tw:flex tw:justify-end">
          <div
            data-tip="Export All as .CSV"
            class="tw:tooltip tw:tooltip-top tw:z-50 tw:flex">
            <AppButton @click="exportCarrierTopBillable">
              <Download class="tw:justify-self-center" :size="15" />
            </AppButton>
          </div>
        </div>
        <div
          class="tw:mt-2"
          v-if="!loadingData && !error">
          <div
            class="tw:m-0 tw:grid tw:h-10 tw:w-full tw:grid-cols-12 tw:gap-2 tw:border-t tw:border-gray-300 tw:bg-[#4D9CB9] tw:pl-2 tw:font-semibold tw:text-white tw:uppercase">
            <div class="tw:col-span-1">&nbsp;</div>
            <div class="tw:col-span-7 tw:flex tw:items-center tw:p-0">Name</div>
            <div class="tw:col-span-4 tw:flex tw:items-center tw:px-0">AM Best Rating</div>
          </div>

          <VirtualList
            :items="filteredList"
            :row-height="50">
            <template #row="{ item }">
              <div
                class="hover:tw:bg-gray-50 tw:grid tw:min-h-[50px] tw:w-full tw:grid-cols-12 tw:items-center tw:gap-2 tw:overflow-visible tw:border-t tw:border-gray-200 tw:pl-2"
                data-test="list-item-row">
                <div class="tw:col-span-1 tw:flex tw:items-center tw:justify-start">
                  <FavoriteIndicator
                    tooltip-position="right"
                    v-model="item.favorite"
                    @update:model-value="toggleFavorite(item)"
                    :loading="item.progress" />
                </div>
                <div class="tw:col-span-7 tw:flex tw:items-center">
                  <router-link
                    class="tw:text-base tw:font-medium tw:tracking-wide tw:text-slate-700 tw:no-underline"
                    :to="`/carrier/${item.ID}/overview`"
                    data-test="list-item-name">
                    {{ item.name }}
                  </router-link>
                </div>
                <div class="tw:col-span-4 tw:flex tw:items-center">
                  <router-link
                    class="tw:text-base tw:tracking-wide tw:text-slate-700 tw:no-underline"
                    :to="`/carrier/${item.ID}/insurer-ratings`"
                    data-test="list-item-rating">
                    {{ item.rating }}
                  </router-link>
                </div>
              </div>
            </template>
          </VirtualList>
        </div>
      </div>

      <div class="tw:pl-2.5 tw:md:col-span-7">
        <div class="mt-4">
          <div class="tw:mx-10 tw:my-2.5 tw:flex tw:items-center tw:justify-between">
            <CoverageTypeFilter
              :coverage-types="coverageTypes"
              v-model="selectedCoverageTypes"
              @update:model-value="refreshData" />
            <CarrierGroupFilter
              class="tw:mr-auto tw:ml-2"
              v-model="selectedCarrierGroups"
              :items="carrierGroups"
              @update:model-value="refreshData" />

            <ManagedDateRange
              class="tw:justify-self-end"
              :initialStartDate="startDate"
              :initialEndDate="endDate"
              :onApply="setDate" />
          </div>

          <div
            v-if="!loadingData"
            data-test="carrier-top-billable-chart">
            <fusioncharts
              type="stackedbar2d"
              width="100%"
              height="680"
              dataFormat="json"
              :dataSource="topBillableDataSource" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import { stackedSeriesColors } from '@/utilities/Color';
import ManagedDateRange from '@/components/ManagedDateRange.vue';
import CoverageTypeFilter from '@/components/CoverageTypeFilter.vue';
import CarrierGroupFilter from '@/components/CarrierGroupFilter.vue';
import { carrierService, favoriteService, referenceService } from '@/services/api';
import FavoriteIndicator from '@/components/FavoriteIndicator';
import TextField from '@/components/v2/TextField.vue';
import VirtualList from '@/components/v2/VirtualList.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import { Download, Search } from 'lucide-vue-next';
import { colorsBySourceAgencyKey } from '@/utilities/Color';
import { agencyLegendOrder } from '@/utilities/Charts';

const topBillableChart = {
  animation: '1',
  caption: 'Total Premium by Carrier Group',
  xAxisName: 'Carrier Group',
  yAxisName: 'Premium',
  theme: 'cacs',
  exportFilename: 'CACS Total Premium By Carrier Group',
  paletteColors: stackedSeriesColors,
  showSum: true,
  showValues: false,
  forceYAxisValueDecimals: false,
  decimals: 1,
};

export default {
  name: 'CarrierList',
  components: {
    FavoriteIndicator,
    ManagedDateRange,
    CoverageTypeFilter,
    CarrierGroupFilter,
    TextField,
    VirtualList,
    AppButton,
    Skeleton,
    Download,
  },
  props: ['crumbs'],
  data: () => ({
    list: [],
    loading: true,
    loadingData: true,
    error: undefined,
    filterText: '',
    searchIcon: Search,
    filterCount: 0,
    showSuccess: false,
    created: false,
    loadingFilters: true,
    selectedCoverageTypes: [],
    selectedCarrierGroups: [],
    expandCoverageTypes: false,
    coverageTypes: [],
    carrierGroups: [],
    topBillableData: { categories: [], dataset: [] },
    startDate: DateTime.local().minus({ years: 1 }).toJSDate(),
    endDate: DateTime.local().toJSDate(),
  }),
  computed: {
    filteredList() {
      return this.filterList();
    },
    topBillableDataSource() {
      return { chart: topBillableChart, ...this.topBillableData };
    },
  },
  methods: {
    async toggleFavorite(item) {
      const updateListProgress = (list, itemID, progress) => list.map((a) => (a.ID === itemID ? { ...a, progress } : a));
      const updateListFavorite = (list, itemID, favorite) => list.map((a) => (a.ID === itemID ? { ...a, favorite } : a));

      this.list = updateListProgress(this.list, item.ID, true);
      if (!item.favorite) {
        // what do we need to remove? - service + object id.
        await favoriteService.remove('carrier', item.ID);
        this.list = updateListFavorite(this.list, item.ID, false);
      } else {
        await favoriteService.create('carrier', item.ID);
        this.list = updateListFavorite(this.list, item.ID, true);
      }
      this.list = updateListProgress(this.list, item.ID, false);
    },

    isPublic(account) {
      return account.ownershipType?.toLowerCase() === 'public';
    },
    anchorHashCheck() {
      if (window.location.hash === this.$route.hash) {
        setTimeout(() => {
          const elements = document.getElementsByName(this.$route.hash.slice(1));
          if (elements.length > 0) {
            elements[0].scrollIntoView({ block: 'center', inline: 'center' });
          }
        }, 1);
      }
    },
    filterList: function () {
      if (!this.filterText) {
        return this.list;
      }
      const parts = this.filterText.toLowerCase().split(' ');
      return this.list.filter((carrier) => {
        for (const part of parts) {
          if (carrier?.name?.toLowerCase()?.includes(part)) {
            return true;
          }
        }
        return false;
      });
    },

    async loadCarrierTopBillable() {
      try {
        const [start, end] = [DateTime.fromJSDate(this.startDate), DateTime.fromJSDate(this.endDate)];

        let result = (await carrierService.findTopBillable(start, end, 25, this.selectedCoverageTypes, this.selectedCarrierGroups)) || [];

        let carriers = Array.from(
          result
            .reduce((carrierSums, result) => {
              const sum = (carrierSums.get(result.label) || 0) + result.value;
              carrierSums.set(result.label, sum);
              return carrierSums;
            }, new Map())
            .entries(),
        )
          .sort((a, b) => b[1] - a[1])
          .map((entry) => ({ label: entry[0] }));

        let data = result.map((r) => ({
          value: r.value,
          categoryIndex: carriers.findIndex((c) => c.label === r.label),
          seriesName: r.agencyName,
        }));

        const agencies = new Map();

        result.forEach((r) => {
          if (!agencies.has(r.sourceAgencyKey)) {
            agencies.set(r.agencyName, { name: r.agencyName, key: r.sourceAgencyKey });
          }
        });

        const sortedAgencies = Array.from(agencies.values()).sort((a, b) =>
          agencyLegendOrder.indexOf(a.key) > agencyLegendOrder.indexOf(b.key) ? 1 : -1,
        );

        //this chart uses the index of the category (carrier) to determine the order of the bars so can't just be pushed and named as label:value
        let series = sortedAgencies.map((x) => ({
          seriesname: x.name,
          color: colorsBySourceAgencyKey.get(x.key),
          data: data.reduce((acc, cur) => {
            if (cur.seriesName === x.name) {
              acc[cur.categoryIndex] = { value: cur.value };
            }
            return acc;
          }, []),
        }));

        this.topBillableData = { categories: [{ category: carriers }], dataset: series };
      } catch (err) {
        console.error(err);
      }
    },
    async exportCarrierTopBillable() {
      const [start, end] = [DateTime.fromJSDate(this.startDate), DateTime.fromJSDate(this.endDate)];
      const carrierIds = this.selectedCarrierGroups.map((cg) => cg.value);
      const data = await carrierService.findTopBillable(start, end, 0, this.selectedCoverageTypes, carrierIds);

      const seriesNames = Array.from(new Set(data.map((d) => d.agencyName)));
      const header = ['Carrier', ...seriesNames].join(',');

      const premiumsMap = new Map();
      data.forEach((d) => {
        if (!premiumsMap.has(d.label)) {
          premiumsMap.set(d.label, {});
        }
        premiumsMap.get(d.label)[d.agencyName] = d.value;
      });

      const csvRows = [header];
      premiumsMap.forEach((seriesData, carrier) => {
        const escapedCarrier = carrier.includes(',') ? `"${carrier}"` : carrier;
        const row = [escapedCarrier];
        seriesNames.forEach((series) => {
          row.push(seriesData[series] ? `$${seriesData[series].toFixed(2)}` : '$0.00');
        });
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Total_Premium_By_Carrier.csv';
      a.click();
    },
    async refreshData() {
      if (this.created && this.loadingData) {
        return;
      }

      this.loadingData = true;
      await Promise.all([this.loadCarrierTopBillable()]);
      this.list = await carrierService.list();

      this.list.sort((a, b) => b.favorite - a.favorite || a.name.localeCompare(b.name));

      this.loadingData = false;

      this.anchorHashCheck();
    },
    setDate(datePayload) {
      if (datePayload.startDate) {
        this.startDate = datePayload.startDate;
      }
      if (datePayload.endDate) {
        this.endDate = datePayload.endDate;
      }
      this.refreshData();
    },

    async refreshFilters() {
      this.loadingFilters = true;
      await Promise.all([this.loadCoverageTypes(), this.loadCarrierGroups()]);
      this.loadingFilters = false;
    },
    async loadCoverageTypes() {
      this.coverageTypes = (await referenceService.coverageTypes())
        .filter((item) => item.label.toLowerCase() !== 'fees')
        .map((ct) => ({ text: ct.label, value: ct.label }));
    },
    async loadCarrierGroups() {
      this.carrierGroups = (await referenceService.carrierGroups()).map((ct) => ({
        text: ct.label,
        value: ct.label,
      }));
    },
  },
  created() {
    void this.refreshFilters();
    void this.refreshData();
    this.created = true;
  },
};
</script>
